import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import {
  addShift,
  deleteShift,
  getAllShifts,
  updateShift,
} from "../../services/services";
import { confirmDelete } from "../../components/confirmDelete";

const Shift = () => {
  const [shifts, setShifts] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    startTime: "",
    endTime: "",
    graceLate: 0,
    graceEarly: 0,
    halfDayThreshold: 0,
    overtimeAfter: 0,
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch all shifts
  const fetchShifts = async () => {
    try {
      setLoading(true);
      const res = await getAllShifts();
      if (res.data.status) {
        setShifts(res.data.response || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load shifts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    if (!formData.name) newErrors.name = "Shift Name is required";
    if (!formData.startTime) newErrors.startTime = "Start Time is required";
    if (!formData.endTime) newErrors.endTime = "End Time is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      const firstError = Object.values(newErrors).find((err) => err);
      if (firstError) toast.error(firstError);

      return;
    }

    try {
      let res;
      if (editingId) {
        res = await updateShift(editingId, formData);
        toast.success("Shift updated successfully");
      } else {
        res = await addShift(formData);
        toast.success("Shift created successfully");
      }

      if (res.data.status) {
        resetForm();
        fetchShifts();
        setIsModalOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Server error");
    }
  };

  const handleEdit = (shift) => {
    setFormData({
      name: shift.name,
      startTime: shift.startTime,
      endTime: shift.endTime,
      graceLate: shift.graceLate || 0,
      graceEarly: shift.graceEarly || 0,
      halfDayThreshold: shift.halfDayThreshold || 0,
      overtimeAfter: shift.overtimeAfter || 0,
    });
    setEditingId(shift._id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id, name) => {
    const isConfirm = await confirmDelete(`${name} shift`);
    if (!isConfirm) return;
    try {
      const res = await deleteShift(id);
      if (res.data.status) {
        toast.success("Shift deleted successfully");
        fetchShifts();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete shift");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      startTime: "",
      endTime: "",
      graceLate: 0,
      graceEarly: 0,
      halfDayThreshold: 0,
      overtimeAfter: 0,
    });
    setEditingId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => <div className="fw-semibold">{row.name}</div>,
      minWidth: "160px",
    },
    {
      name: "Time",
      selector: (row) => `${row.startTime} - ${row.endTime}`,
      cell: (row) => (
        <div>
          {row.startTime} - {row.endTime}
        </div>
      ),
      minWidth: "140px",
    },
    {
      name: "Grace Late",
      selector: (row) => row.graceLate,
      cell: (row) => <div>{row.graceLate} min</div>,
      center: true,
      minWidth: "110px",
    },
    {
      name: "Grace Early",
      selector: (row) => row.graceEarly,
      cell: (row) => <div>{row.graceEarly} min</div>,
      center: true,
      minWidth: "110px",
    },
    {
      name: "Half Day",
      selector: (row) => row.halfDayThreshold,
      cell: (row) => (
        <div>{row.halfDayThreshold ? `${row.halfDayThreshold} hrs` : "-"}</div>
      ),
      center: true,
      minWidth: "110px",
    },
    {
      name: "Overtime After",
      selector: (row) => row.overtimeAfter,
      cell: (row) => (
        <div>{row.overtimeAfter ? `${row.overtimeAfter} hrs` : "-"}</div>
      ),
      center: true,
      minWidth: "130px",
    },
    {
      name: "Actions",
      center: true,
      minWidth: "140px",
      cell: (row) => (
        <div style={{ whiteSpace: "nowrap" }}>
          <button
            className="btn btn-sm btn-outline-primary me-2"
            onClick={() => handleEdit(row)}
            title="Edit Shift"
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDelete(row._id, row.name)}
            title="Delete Shift"
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        color: "#fff",
        borderBottom: "1px solid #dee2e6",
      },
    },
    headCells: {
      style: {
        fontWeight: 500,
        fontSize: "0.95rem",
      },
    },
    rows: {
      style: {
        minHeight: "48px",
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
      },
    },
    cells: {
      style: {
        padding: "0.75rem 1rem",
      },
    },
  };

  return (
    <div id="main">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="custom-title mb-0">Manage Shifts</h4>
        <button onClick={openCreateModal} className="btn btn-primary">
          Create Shift
        </button>
      </div>

      {/* Table / DataTable Section */}
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow">
            <div className="card-body p-0">
              {loading ? (
                <div className="p-5 text-center">
                  <div
                    className="spinner-border text-primary mb-3"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div>Loading shifts...</div>
                </div>
              ) : shifts.length === 0 ? (
                <div className="p-5 text-center text-muted">
                  <i className="bi bi-clock-history fs-1 mb-3 d-block"></i>
                  <p className="mb-0 fs-5">No shifts created yet</p>
                  <small>Click "Create Shift" to add one</small>
                </div>
              ) : (
                <div className="table-responsive rounded">
                  <DataTable
                    columns={columns}
                    data={shifts}
                    customStyles={customStyles}
                    highlightOnHover
                    pointerOnHover
                    noHeader
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal - unchanged */}
      {isModalOpen && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  {editingId ? "Edit Shift" : "Create New Shift"}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setIsModalOpen(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">
                      Shift Name
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? "border-danger shadow-none" : ""}`}
                      name="name"
                      value={formData.name}
                      onChange={(e) => {
                        handleChange(e);
                        setErrors({ ...errors, name: "" });
                      }}
                      placeholder="e.g. Day Shift"
                    />
                    {errors.name && (
                      <small className="text-danger">{errors.name}</small>
                    )}
                  </div>

                  <div className="row">
                    <div className="col-6 mb-3">
                      <label className="form-label">
                        Start Time
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="time"
                        className={`form-control ${errors.startTime ? "border-danger shadow-none" : ""}`}
                        name="startTime"
                        value={formData.startTime}
                        onChange={(e) => {
                          handleChange(e);
                          setErrors({ ...errors, startTime: "" });
                        }}
                      />
                      {errors.startTime && (
                        <small className="text-danger">
                          {errors.startTime}
                        </small>
                      )}
                    </div>
                    <div className="col-6 mb-3">
                      <label className="form-label">
                        End Time
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="time"
                        className={`form-control ${errors.endTime ? "border-danger shadow-none" : ""}`}
                        name="endTime"
                        value={formData.endTime}
                        onChange={(e) => {
                          handleChange(e);
                          setErrors({ ...errors, endTime: "" });
                        }}
                      />
                      {errors.endTime && (
                        <small className="text-danger">{errors.endTime}</small>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6 mb-3">
                      <label className="form-label">Grace Late (minutes)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="graceLate"
                        value={formData.graceLate}
                        onChange={handleChange}
                        min="0"
                        placeholder="e.g. 15"
                      />
                    </div>

                    <div className="col-6 mb-3">
                      <label className="form-label">
                        Grace Early Departure (minutes)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="graceEarly"
                        value={formData.graceEarly}
                        onChange={handleChange}
                        min="0"
                        placeholder="e.g. 10"
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 mb-3">
                      <label className="form-label">
                        Half Day Threshold (hours)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="halfDayThreshold"
                        value={formData.halfDayThreshold}
                        onChange={handleChange}
                        min="0"
                        step="0.5"
                        placeholder="e.g. 4"
                      />
                    </div>

                    <div className="col-6 mb-3">
                      <label className="form-label">
                        Overtime After (hours)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="overtimeAfter"
                        value={formData.overtimeAfter}
                        onChange={handleChange}
                        min="0"
                        step="0.5"
                        placeholder="e.g. 8"
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {editingId ? "Update" : "Create"} Shift
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default headerLayoutWrapper(Shift);
